import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppLayout from './Layout';
import { useOrganization } from '../context/OrganizationContext';
import CybeSelect from "./CybeSelect";

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' }
];

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function statusBadgeStyle(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'completed') return { background: '#dcfce7', color: '#166534' };
  if (value === 'processing') return { background: '#dbeafe', color: '#1d4ed8' };
  if (value === 'queued') return { background: '#fef3c7', color: '#92400e' };
  if (value === 'failed') return { background: '#fee2e2', color: '#991b1b' };
  return { background: '#e5e7eb', color: '#374151' };
}

function formatThreatSummary(item) {
  const counts = item?.result_summary?.threat_counts;
  if (counts && typeof counts === 'object' && !Array.isArray(counts)) {
    const entries = Object.entries(counts)
      .filter(([, count]) => Number.isFinite(Number(count)))
      .sort((a, b) => Number(b[1]) - Number(a[1]));

    if (entries.length > 0) {
      return entries.map(([label, count]) => `${label}: ${count}`).join(', ');
    }
  }

  if (item?.failure?.code === 'processing_interrupted') {
    return 'Processing interrupted';
  }
  if (item?.failure?.code === 'processing_failed') {
    return 'Processing failed';
  }
  return '-';
}

function formatConfidence(item) {
  const raw = Number(item?.result_summary?.avg_confidence);
  if (!Number.isFinite(raw)) return '-';
  const percent = raw <= 1 ? raw * 100 : raw;
  return `${percent.toFixed(1)}%`;
}

export default function IngestionsPage({ onLogout }) {
  const {
    activeOrganization,
    organizationLoading,
    organizationError
  } = useOrganization();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const loadData = useCallback(async () => {
    const organizationGuid = activeOrganization?.guid;
    if (!organizationGuid) {
      setItems([]);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setItems([]);
      setError('Authentication required.');
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL || '/api';
      const query = new URLSearchParams({ page: '1', page_size: '100' });
      if (statusFilter) query.set('status', statusFilter);

      const response = await fetch(
        `${baseUrl}/cybedefender/organizations/${encodeURIComponent(organizationGuid)}/ingestions?${query.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Ingestions request failed: ${response.status}`);
      }

      const data = await response.json();
      if (requestId === requestIdRef.current) {
        setItems(Array.isArray(data.ingestions) ? data.ingestions : []);
      }
    } catch (loadError) {
      if (requestId === requestIdRef.current) {
        setItems([]);
        setError(loadError.message || 'Failed to load ingestions.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeOrganization?.guid, statusFilter]);

  useEffect(() => {
    requestIdRef.current += 1;
    setItems([]);
    setError('');

    if (!activeOrganization?.guid) return undefined;

    loadData();
    const timer = setInterval(loadData, 15000);

    return () => {
      clearInterval(timer);
      requestIdRef.current += 1;
    };
  }, [activeOrganization?.guid, loadData]);

  let emptyMessage = 'No ingestions found for this organization.';
  if (organizationLoading) emptyMessage = 'Loading organization...';
  else if (!activeOrganization) emptyMessage = 'No organization is available for this account.';
  else if (loading) emptyMessage = 'Loading ingestions...';

  return (
    <AppLayout onLogout={onLogout}>
            <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Ingestions</h2>
            <p className="mt-1 text-sm text-slate-500">
              Tenant-owned ingestion jobs and safe processing summaries.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <CybeSelect
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              disabled={!activeOrganization}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Filter ingestions by status"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </CybeSelect>
            <button
              type="button"
              onClick={loadData}
              disabled={!activeOrganization || loading}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {(organizationError || error) && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {organizationError || error}
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="p-3 text-left font-medium">Job ID</th>
                <th className="p-3 text-left font-medium">Device</th>
                <th className="p-3 text-left font-medium">Filename</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Events</th>
                <th className="p-3 text-left font-medium">Threat Summary</th>
                <th className="p-3 text-left font-medium">Avg. Confidence</th>
                <th className="p-3 text-left font-medium">Received</th>
                <th className="p-3 text-left font-medium">Completed</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td className="p-6 text-center text-slate-500" colSpan="9">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.job_id} className="border-t border-slate-100 align-top">
                    <td className="p-3 font-mono text-xs text-slate-700">{item.job_id || '-'}</td>
                    <td className="p-3 text-slate-700">{item.device?.display_name || '-'}</td>
                    <td className="p-3 text-slate-600">{item.filename || '-'}</td>
                    <td className="p-3">
                      <span
                        className="rounded-full px-3 py-1 text-xs font-semibold capitalize"
                        style={statusBadgeStyle(item.status)}
                      >
                        {item.status || '-'}
                      </span>
                      {item.failure?.manual_reingest_required && (
                        <div className="mt-2 text-xs text-amber-700">
                          Manual re-ingestion required
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">{item.event_count ?? 0}</td>
                    <td className="max-w-xs p-3 text-slate-600">{formatThreatSummary(item)}</td>
                    <td className="p-3 text-slate-600">{formatConfidence(item)}</td>
                    <td className="p-3 text-slate-600">{formatDate(item.received_at)}</td>
                    <td className="p-3 text-slate-600">{formatDate(item.completed_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
          </AppLayout>
  );
}
